import {Entity, EntityModel} from './entity.model';
import {OrganizationRole} from './organization.model';

/**
 * Organization membership entity as returned from the API.
 * Represents the relationship between a user and an organization.
 * 
 * Note: The `id` field represents the user's ID, not the membership ID.
 * This allows operations to be performed using the user as the identifier.
 */
export interface OrganizationMembership extends Entity {
  /** User's role in the organization */
  role: OrganizationRole;
  /** ISO timestamp when user joined the organization */
  created_at: string;
  /** User information */
  name: string;
  email: string;
}

/**
 * Organization membership model with convenience methods.
 */
export class OrganizationMembershipModel extends EntityModel<OrganizationMembership> {
  readonly role: OrganizationRole;
  readonly createdAt: Date;
  readonly name: string;
  readonly email: string;

  constructor(source: OrganizationMembership) {
    super(source);
    this.role = source.role;
    this.createdAt = new Date(source.created_at);
    this.name = source.name;
    this.email = source.email;
  }

  /**
   * Check if this membership has admin role
   */
  get isAdmin(): boolean {
    return this.role === OrganizationRole.Admin;
  }
}

/**
 * Data required to add a new member to an organization.
 * Members are added by email address.
 */
export interface CreateMembership {
  /** Email address of the user to add */
  email: string;
  /** Optional role (defaults to reader if not specified) */
  role?: OrganizationRole;
}

/**
 * Data required to update a member's role in an organization
 */
export interface UpdateMembership {
  /** New role for the member */
  role: OrganizationRole;
}
